import * as React from 'react';
import { observer } from 'src/utils/mobx-react';
import {
    EventHeaderCaptionName,
    EventHeaderCaption,
    EventHeaderCaptionWrapper,
    EventHeaderLabel,
} from 'src/domains/sportsbook/webview/components/sportEventsListNew/EventGroupHeaderUnified.style';
import { Collapse } from 'src/ui/components/collapse/sports/05/Collapse';
import { shouldUseExpandedSelectionLayout } from 'src/domains/sportsbook/state/eventsListState/unifiedSportEventsListState/UnifiedEventsListMarkets';

interface PropsType {
    label?: string;
    selections: Array<string>;
    open: boolean;
    onToggle: () => void;
    dataTest?: string;
    className?: string;
    showCompetitonLabel?: boolean;
    /** When set, expanded layout (boxing/mma/tennis) is derived automatically. */
    sportId?: string;
    /** Explicit override; when omitted, derived from `sportId`. */
    isExpandedSelectionLayout?: boolean;
    showHeader?: boolean;
}

const resolveIsExpandedSelectionLayout = (
    sportId: string | undefined,
    isExpandedSelectionLayout: boolean | undefined
): boolean => {
    if (isExpandedSelectionLayout !== undefined) {
        return isExpandedSelectionLayout;
    }
    return sportId !== undefined && shouldUseExpandedSelectionLayout(sportId);
};

export const EventHeader = ({
    showCompetitonLabel = false,
    label,
    selections,
    className,
    sportId,
    isExpandedSelectionLayout,
    showHeader = true,
}: Omit<PropsType, 'onToggle' | 'open'>) => {
    const resolvedIsExpandedSelectionLayout = resolveIsExpandedSelectionLayout(sportId, isExpandedSelectionLayout);

    return showHeader && (showCompetitonLabel || selections.length > 0) ? (
        <EventHeaderCaptionWrapper
            className={className}
            dataTest='event-header-caption'
            isExpandedSelectionLayout={resolvedIsExpandedSelectionLayout}
        >
            {showCompetitonLabel && typeof label === 'string' ? <EventHeaderLabel>{label}</EventHeaderLabel> : null}
            {selections.length > 1
                ? selections.map((name, key) => (
                      <EventHeaderCaption
                          key={key}
                          isExpandedSelectionLayout={resolvedIsExpandedSelectionLayout}
                      >
                          <EventHeaderCaptionName>{name}</EventHeaderCaptionName>
                      </EventHeaderCaption>
                  ))
                : null}
        </EventHeaderCaptionWrapper>
    ) : null;
};

export const EventGroupHeaderUnified = observer('EventGroupHeader', (props: PropsType) => {
    const {
        dataTest,
        label,
        onToggle,
        open,
        selections,
        showCompetitonLabel = false,
        sportId,
        isExpandedSelectionLayout,
        showHeader = true,
    } = props;

    return (
        <>
            {showCompetitonLabel === false ? (
                <Collapse
                    dataTest={dataTest}
                    isOpen={open}
                    handleToggle={onToggle}
                    title={label}
                />
            ) : null}
            {open && (
                <EventHeader
                    showCompetitonLabel={showCompetitonLabel}
                    selections={selections}
                    label={label}
                    sportId={sportId}
                    isExpandedSelectionLayout={isExpandedSelectionLayout}
                    showHeader={showHeader}
                />
            )}
        </>
    );
});
